import { Check } from "lucide-react";
import { LegalLinks } from "../legal/LegalLinks";
import { FreeForever } from "../../components/FreeForever";
import { useI18n } from "../../lib/i18n";
import { LangPicker } from "../../components/LangPicker";
import { Link } from "../../lib/router";
import { LogoMark } from "../../components/Logo";
import { Mini, type Area } from "../../components/Tactile";
import { AskHelper } from "./AskHelper";
import "./landing.css";

/** Splits a headline at its last comma so the second half can carry the accent colour. */
function splitTitle(s: string): [string, string] {
  const i = Math.max(s.lastIndexOf("，"), s.lastIndexOf(","), s.lastIndexOf("："), s.lastIndexOf(":"));
  if (i <= 0 || i >= s.length - 2) return [s, ""];
  return [s.slice(0, i + 1).trim(), s.slice(i + 1).trim()];
}

/**
 * The first page visitors see. Built like the welcome screens of the best-loved apps: one picture that shows
 * the product, one headline that says what it does, and the two buttons — sign up and sign in — straight
 * under it, all on the first screen. Everything else (what each part does, what stays free, questions)
 * comes after, for those who scroll.
 */
export default function LandingPage() {
  const { t, lang, setPref } = useI18n();
  const [head, accent] = splitTitle(t("landing.title"));

  // The floating tiles in the picture: the app's own icons, as they look inside.
  const stage: Area[] = ["shop", "receipt", "food", "wallet", "ledger", "fridge"];
  const features: { area: Area; title: string; sub: string }[] = [
    { area: "shop", title: t("home.shopping"), sub: t("home.shopping.sub") },
    { area: "receipt", title: t("home.receipt"), sub: t("home.receipt.sub") },
    { area: "ledger", title: t("home.ledger"), sub: t("home.ledger.sub") },
    { area: "food", title: t("home.meals"), sub: t("home.meals.sub") },
    { area: "fridge", title: t("home.fridge"), sub: t("home.fridge.sub") },
    { area: "wallet", title: t("home.cards"), sub: t("home.cards.sub") },
  ];
  const ticks = t("landing.note")
    .split(/\s*[·•]\s*/)
    .filter(Boolean);

  return (
    <div className="landing">
      <header className="landing-bar">
        <div className="landing-bar-inner">
          <span className="brand">
            <span className="brand-mark">
              <LogoMark size={22} />
            </span>
            NutriCart
          </span>
          <div className="landing-bar-right">
            <LangPicker value={lang} onChange={(v) => setPref(v)} compact />
            <Link to="/login" className="pill-btn">
              {t("landing.login")}
            </Link>
          </div>
        </div>
      </header>

      <main>
        <section className="lp-hero">
          <div className="lp-stage" aria-hidden="true">
            <div className="lp-glow" />
            <div className="lp-tiles">
              {stage.map((a, i) => (
                <span key={a} className={`lp-tile lp-tile-${i}`}>
                  <Mini area={a} size="lg" />
                </span>
              ))}
            </div>
          </div>

          <div className="lp-copy">
            <span className="lp-eyebrow">{t("landing.eyebrow")}</span>
            <h1 className="lp-title">
              {head}
              {accent && <span className="lp-accent">{accent}</span>}
            </h1>
            <p className="lp-sub">{t("landing.sub")}</p>
            <div className="lp-actions">
              <Link to="/register" className="btn lp-primary">
                {t("landing.signup")}
              </Link>
              <Link to="/login" className="btn secondary lp-secondary">
                {t("landing.haveAccount")}
              </Link>
            </div>
            <ul className="lp-ticks">
              {ticks.map((x) => (
                <li key={x}>
                  <Check size={16} strokeWidth={3} aria-hidden="true" /> {x}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="lp-section" aria-labelledby="lp-features">
          <h2 id="lp-features" className="lp-h2">
            {t("landing.featuresTitle")}
          </h2>
          <p className="lp-lead">{t("landing.lead")}</p>
          <ul className="lp-features">
            {features.map((f) => (
              <li key={f.area} className="lp-feature">
                <Mini area={f.area} />
                <span>
                  <strong>{f.title}</strong>
                  <small>{f.sub}</small>
                </span>
              </li>
            ))}
          </ul>
        </section>

        <section className="lp-section">
          <FreeForever links={false} />
          <p className="lp-plus">{t("landing.plusLine")}</p>
        </section>

        {/* Questions before signing up, answered at once by the AI helper. */}
        <AskHelper />

        <section className="lp-final">
          <h2>{t("landing.ctaTitle")}</h2>
          <div className="lp-actions">
            <Link to="/register" className="btn lp-primary light">
              {t("landing.signup")}
            </Link>
            <Link to="/login" className="lp-final-login">
              {t("landing.haveAccount")}
            </Link>
          </div>
        </section>
      </main>

      <footer className="landing-footer">
        <span>{t("landing.footer")}</span>
        <LegalLinks />
      </footer>
    </div>
  );
}
